import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import {
  dateFormatter,
  dateTimeFormatter,
  getFormattedRuPhone,
} from 'core/utils/commonUtils';
import { useConfirm } from 'material-ui-confirm';
import useMangoCalls, { SWR_MANGO_CALLS_ENDPOINT } from 'hooks/swr/useMangoCalls';
import useResponsive from 'hooks/useResponsive';
import CardedPage from 'layout/pages/CardedPage';
import { isEmpty, isArray } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { mutate } from 'swr';
import { createMangoCallOrderAsync } from 'services/mangoCallService';
import MangoCallsPageHeader from './MangoCallsPageHeader';
import MangoCallsPlayer from 'core/components/MangoCallsPlayer';
import type { MangoCallsFilterData } from './MangoCallsPageHeader';
import type { GridColDef } from '@mui/x-data-grid';
import type { MangoCallInfo } from 'models/mangoCall';
import type { MangoCallsPlayerHandle } from 'core/components/MangoCallsPlayer';
import type { OrderStateEnum } from 'models/enums/orderStateEnum';

const ORDER_PATHS = ['/requests', '/orders', '/archive'] as const;

function getDefaultDateRange(): { start: Date; end: Date } {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function getOrderPath(state: OrderStateEnum, id: number): string {
  return `${ORDER_PATHS[state]}/${id}`;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function ParticipantInfo({
  call,
  side,
}: {
  call: MangoCallInfo;
  side: 'from' | 'to';
}): JSX.Element {
  const phone = getFormattedRuPhone(side === 'from' ? call.fromNumber : call.toNumber);
  const name =
    side === 'from'
      ? call.fromUser?.fullName ?? call.fromClient?.fullName
      : call.toUser?.fullName ?? call.toClient?.fullName;

  return (
    <Box>
      <Typography variant='body2' lineHeight={1.3}>
        {name ?? phone}
      </Typography>
      {name != null && (
        <Typography
          variant='caption'
          color='text.secondary'
          display='block'
          lineHeight={1.3}>
          {phone}
        </Typography>
      )}
    </Box>
  );
}

export default function MangoCallsPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSmScreen = useResponsive('down', 'sm');
  const [filters, setFilters] = useState<{
    pagination: {
      page: number;
      pageSize: number;
    };
    searchText: string;
    fromDate: Date | null;
    toDate: Date | null;
  }>(() => {
    const { start, end } = getDefaultDateRange();
    return {
      pagination: {
        page: +(searchParams.get('pageIndex') ?? 0),
        pageSize: +(searchParams.get('pageSize') ?? 50),
      },
      searchText: searchParams.get('searchText') ?? '',
      fromDate: searchParams.get('from')
        ? new Date(searchParams.get('from') ?? '')
        : start,
      toDate: searchParams.get('to') ? new Date(searchParams.get('to') ?? '') : end,
    };
  });

  const [selectedCall, setSelectedCall] = useState<MangoCallInfo | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const playerRef = useRef<MangoCallsPlayerHandle>(null);
  const [creatingOrderFor, setCreatingOrderFor] = useState<number | null>(null);
  const confirm = useConfirm();

  const handleCreateOrder = async (call: MangoCallInfo): Promise<void> => {
    const caller =
      call.fromUser?.fullName ??
      call.fromClient?.fullName ??
      getFormattedRuPhone(call.fromNumber);
    const date = dateTimeFormatter.format(call.dateOfCall);
    await confirm({
      description: `Создать заявку по звонку от ${caller} (${date})?`,
    }).catch(() => {
      throw new Error('cancelled');
    });
    setCreatingOrderFor(call.id);
    try {
      await createMangoCallOrderAsync(call.id);
      await mutate(
        (key) => isArray(key) && key[0] === SWR_MANGO_CALLS_ENDPOINT,
        undefined
      );
    } finally {
      setCreatingOrderFor(null);
    }
  };

  useEffect(() => {
    setSearchParams({
      pageIndex: String(filters.pagination.page),
      pageSize: String(filters.pagination.pageSize),
      searchText: filters.searchText,
      from: filters.fromDate?.toISOString() ?? '',
      to: filters.toDate?.toISOString() ?? '',
    });
  }, [setSearchParams, filters]);

  const { data: calls, isLoading } = useMangoCalls({
    pageIndex: filters.pagination.page,
    pageSize: filters.pagination.pageSize,
    from: filters.fromDate ?? undefined,
    to: filters.toDate ?? undefined,
  });

  const filterChangeHandler = (data: MangoCallsFilterData): void => {
    setFilters({ ...filters, ...data, searchText: data.filterText ?? '' });
  };

  const currentPageItems = calls?.items ?? [];
  const currentIndex = selectedCall
    ? currentPageItems.findIndex((c) => c.id === selectedCall.id)
    : -1;
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex >= 0 && currentIndex < currentPageItems.length - 1;

  const handlePrev = (): void => {
    if (canGoPrev) setSelectedCall(currentPageItems[currentIndex - 1] ?? null);
  };

  const handleNext = (): void => {
    if (canGoNext) setSelectedCall(currentPageItems[currentIndex + 1] ?? null);
  };

  const title = 'Звонки';

  const columns: Array<GridColDef<MangoCallInfo>> = [
    {
      field: 'dateOfCall',
      headerName: 'Дата звонка',
      type: 'dateTime',
      minWidth: 170,
      valueFormatter: (value: Date) => dateTimeFormatter.format(value),
    },
    {
      field: 'fromNumber',
      headerName: 'От кого',
      flex: 0.5,
      minWidth: 160,
      renderCell: (params) => <ParticipantInfo call={params.row} side='from' />,
    },
    {
      field: 'toNumber',
      headerName: 'Кому',
      flex: 0.5,
      minWidth: 160,
      renderCell: (params) => <ParticipantInfo call={params.row} side='to' />,
    },
    {
      field: 'duration',
      headerName: 'Длительность',
      minWidth: 120,
      valueFormatter: (value: number) => formatDuration(value),
    },
    {
      field: 'order',
      headerName: 'Заказ',
      flex: 0.2,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => {
        const { order } = params.row;
        if (order != null) {
          return (
            <Button
              component={RouterLink}
              to={getOrderPath(order.state, order.id)}
              size='small'
              variant='text'>
              {order.number}
            </Button>
          );
        }
        return null;
      },
    },
    {
      field: 'recordUrl',
      headerName: 'Запись',
      minWidth: 80,
      sortable: false,
      renderCell: (params) => {
        const isSelected = selectedCall?.id === params.row.id;
        const isRowPlaying = isSelected && isPlaying;
        return !isEmpty(params.value) ? (
          <IconButton
            size='small'
            onClick={() => {
              if (isSelected) {
                playerRef.current?.togglePlay();
              } else {
                setSelectedCall(params.row);
              }
            }}
            color={isSelected ? 'primary' : 'default'}
            sx={
              isRowPlaying
                ? {
                    '@keyframes pulse': {
                      '0%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.4)' },
                      '70%': { boxShadow: '0 0 0 8px rgba(25, 118, 210, 0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' },
                    },
                    animation: 'pulse 1.5s ease-out infinite',
                    borderRadius: '50%',
                  }
                : undefined
            }>
            {isRowPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        ) : null;
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 56,
      sortable: false,
      renderCell: (params) => {
        if (params.row.order !== null) return null;
        const isCreating = creatingOrderFor === params.row.id;
        return (
          <Tooltip title='Создать заявку'>
            <span>
              <IconButton
                size='small'
                disabled={isCreating}
                onClick={() => {
                  void handleCreateOrder(params.row);
                }}>
                {isCreating ? (
                  <CircularProgress size={18} />
                ) : (
                  <NoteAddIcon fontSize='small' />
                )}
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const mobileView = (
    <List disablePadding>
      {currentPageItems.map((call) => (
        <ListItem
          key={call.id}
          divider
          secondaryAction={
            !isEmpty(call.recordUrl) ? (
              <IconButton
                size='small'
                onClick={() => {
                  if (selectedCall?.id === call.id) {
                    playerRef.current?.togglePlay();
                  } else {
                    setSelectedCall(call);
                  }
                }}
                color={selectedCall?.id === call.id ? 'primary' : 'default'}
                sx={
                  selectedCall?.id === call.id && isPlaying
                    ? {
                        '@keyframes pulse': {
                          '0%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.4)' },
                          '70%': { boxShadow: '0 0 0 8px rgba(25, 118, 210, 0)' },
                          '100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' },
                        },
                        animation: 'pulse 1.5s ease-out infinite',
                        borderRadius: '50%',
                      }
                    : undefined
                }>
                {selectedCall?.id === call.id && isPlaying ? (
                  <PauseIcon />
                ) : (
                  <PlayArrowIcon />
                )}
              </IconButton>
            ) : undefined
          }>
          <ListItemText
            disableTypography
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                <ParticipantInfo call={call} side='from' />
                <ArrowForwardIcon
                  sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                />
                <ParticipantInfo call={call} side='to' />
              </Box>
            }
            secondary={
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant='caption' color='text.secondary'>
                  {`${dateFormatter.format(call.dateOfCall)} · ${formatDuration(
                    call.duration
                  )}`}
                </Typography>
                {call.order != null ? (
                  <Button
                    component={RouterLink}
                    to={getOrderPath(call.order.state, call.order.id)}
                    size='small'
                    variant='text'
                    sx={{ p: 0, minWidth: 0, fontSize: 'caption.fontSize' }}>
                    {call.order.number}
                  </Button>
                ) : (
                  <Button
                    size='small'
                    variant='outlined'
                    disabled={creatingOrderFor === call.id}
                    startIcon={
                      creatingOrderFor === call.id ? (
                        <CircularProgress size={10} />
                      ) : undefined
                    }
                    sx={{ py: 0, fontSize: 'caption.fontSize', lineHeight: 1.5 }}
                    onClick={() => {
                      void handleCreateOrder(call);
                    }}>
                    Создать заявку
                  </Button>
                )}
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  );

  return (
    <>
      <CardedPage
        title={title}
        disablePadding
        header={
          <MangoCallsPageHeader
            title={title}
            onFilterChanged={filterChangeHandler}
            filterTextInit={filters.searchText}
            fromDateInit={filters.fromDate}
            toDateInit={filters.toDate}
          />
        }>
        {isSmScreen ? (
          mobileView
        ) : (
          <DataGrid
            rows={currentPageItems}
            columns={columns}
            loading={isLoading}
            autoHeight
            getRowHeight={() => 'auto'}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
              },
            }}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            disableColumnSorting
            paginationModel={filters.pagination}
            onPaginationModelChange={(model) => {
              setFilters({ ...filters, pagination: model });
            }}
            paginationMode='server'
            rowCount={calls?.totalCount ?? 0}
          />
        )}
      </CardedPage>

      {selectedCall != null && (
        <>
          <Box sx={{ height: '130px', flexShrink: 0 }} />
          <MangoCallsPlayer
            ref={playerRef}
            call={selectedCall}
            showSkipControls={currentIndex !== -1}
            onPrev={handlePrev}
            onNext={handleNext}
            onClose={() => {
              setSelectedCall(null);
            }}
            onPlayStateChange={setIsPlaying}
          />
        </>
      )}
    </>
  );
}
